import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "./useAuthSession.ts";

export interface PhantomStatusState {
  hasActiveSession: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const PhantomStatusContext = createContext<PhantomStatusState | null>(null);

interface PhantomStatusProviderProps {
  children: ReactNode;
}

// Tracks whether the authenticated user has at least one open Phantom Bet
// session (a phantom_sessions row with completed_at IS NULL).
//
// Phase 1A AuthGate consumes this alongside users.phantom_opt_out_at to
// decide whether the Phantom step is satisfied. Adding a dedicated
// users.phantom_session_started_at column would have been an extra source
// of truth to keep in sync; querying phantom_sessions directly is simpler
// and avoids drift. RLS limits the query to the caller's own rows.
//
// The provider exposes a refetch() so PhantomBetPage can refresh the state
// after activation; AuthGate then re-renders and forwards the user onward.
export function PhantomStatusProvider({ children }: PhantomStatusProviderProps): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setHasActiveSession(false);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("phantom_sessions")
      .select("id")
      .eq("user_id", user.id)
      .is("completed_at", null)
      .limit(1);
    if (fetchError) {
      setHasActiveSession(false);
      setError(fetchError.message);
    } else {
      setHasActiveSession((data?.length ?? 0) > 0);
    }
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<PhantomStatusState>(
    () => ({ hasActiveSession, loading, error, refetch }),
    [hasActiveSession, loading, error, refetch],
  );

  return <PhantomStatusContext.Provider value={value}>{children}</PhantomStatusContext.Provider>;
}
